import React, { useEffect, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import * as Actions from '../redux/actions';
import Autocomplete from '../components/Autocomplete';
import '../styles/report.scss';

const ReportNew = (props) => {
  const [accountSelected, setAccountSelected] = useState(null);
  const [accountLabel, setAccountLabel] = useState('');
  const [foundDrafts, setFoundDrafts] = useState(null);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [templates, setTemplates] = useState([]);
  const selectingRef = useRef(false);
  const selectedRef = useRef(null);

  const operatorAccount = props.user?.operator?.customer;

  useEffect(() => {
    props.fetchAccounts(props.authToken);
  }, [props.authToken]);

  useEffect(() => {
    if (operatorAccount?.id) {
      handleSelectAccount({ id: operatorAccount.id, label: operatorAccount.name });
    }
  }, [operatorAccount?.id]);

  const clearCustomer = () => {
    selectedRef.current = null;
    setAccountSelected(null);
    setAccountLabel('');
    setFoundDrafts(null);
    setLoadingDrafts(false);
    setTemplates([]);
  };

  const handleSelectAccount = (account) => {
    selectingRef.current = true;
    selectedRef.current = account.id;
    setAccountSelected(account.id);
    setAccountLabel(account.label);
    setFoundDrafts(null);
    setLoadingDrafts(true);
    setTemplates([]);

    // Operators always get their assigned template, so they have nothing to pick.
    if (!operatorAccount) {
      props.fetchReportTemplateOptions(account.id, props.authToken)
        .then((json) => {
          if (selectedRef.current === account.id) setTemplates(json.templates || []);
        })
        .catch(() => {});
    }

    Promise.resolve(
      props.fetchReportDrafts(account.id, props.authToken, (drafts) => {
        setFoundDrafts(drafts || []);
      }, { replaceGlobal: false })
    ).finally(() => setLoadingDrafts(false));
  };

  const handleSearchChange = (next) => {
    setAccountLabel(next);

    // Autocomplete calls onChange after onSelect with the chosen label.
    // Skip clearing so a re-selection doesn't wipe the new customer.
    if (selectingRef.current) {
      selectingRef.current = false;
      return;
    }

    if (accountSelected) {
      selectedRef.current = null;
      setAccountSelected(null);
      setFoundDrafts(null);
      setTemplates([]);
    }
  };

  const newReport = (templateId) => {
    if (!accountSelected) return;

    props.createReport(
      accountSelected,
      'SR',
      null,
      props.authToken,
      (newReportUrl) => {
        window.location.href = `/reports/edit?${newReportUrl}`;
      },
      templateId
    );
  };

  const loadDraft = (url) => {
    window.location.href = `/reports/edit?${url}`;
  };

  const deleteDraft = (e, draft) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft report?')) return;

    props.destroyReport(draft.url, props.authToken, () => {
      setFoundDrafts((prev) => (prev || []).filter((d) => d.id !== draft.id));
    });
  };

  const accounts = (props.accounts || []).map((a) => ({
    id: a.id,
    label: a.name,
  }));

  const hasDrafts = Array.isArray(foundDrafts) && foundDrafts.length > 0;

  return (
    <div className="report-new">
      <div className="report-new-primary">
        {operatorAccount ? (
          <>
            <p className="report-new-kicker">Operator report</p>
            <h2 className="report-new-title">{operatorAccount.name}</h2>
            <p className="report-new-subtitle">
              Start a new report or continue a draft.
            </p>
          </>
        ) : (
          <>
            <p className="report-new-kicker">Service report</p>
            <h2 className="report-new-title">Select a customer</h2>
            <p className="report-new-subtitle">
              Search by account name, then start a new report or continue a draft.
            </p>
          </>
        )}

        {!operatorAccount && <div className={`report-new-search${accountSelected ? ' has-clear' : ''}`}>
          {accounts.length > 0 ? (
            <Autocomplete
              list={accounts}
              value={accountLabel}
              onChange={handleSearchChange}
              onSelect={handleSelectAccount}
              placeholder="Search customers…"
              searchIcon
            />
          ) : (
            <div className="report-new-search-placeholder">Loading customers…</div>
          )}

          {accountSelected && (
            <button
              type="button"
              className="report-new-clear"
              onClick={clearCustomer}
              aria-label="Clear customer"
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>}
      </div>

      {accountSelected && (
        <div className="report-new-secondary">
          <button
            type="button"
            className="report-new-create"
            onClick={() => newReport()}
          >
            <AddIcon className="report-new-create-icon" />
            New report
          </button>

          {templates.length > 0 && (
            <aside className="report-new-drafts" aria-label="Report templates">
              <div className="report-new-drafts-header">
                <span className="report-new-drafts-label">New report from a template</span>
                <span className="report-new-drafts-count">{templates.length}</span>
              </div>
              <ul className="report-new-drafts-list">
                {templates.map((template) => (
                  <li key={template.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      className="report-new-draft-row"
                      onClick={() => newReport(template.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          newReport(template.id);
                        }
                      }}
                    >
                      <div className="report-new-draft-main">
                        <div className="report-new-draft-title-line">
                          <span className="report-new-draft-title">{template.name}</span>
                        </div>
                        <span className="report-new-draft-meta">
                          {template.systems} systems · {template.tests} tests
                        </span>
                      </div>
                      <AddIcon className="report-new-draft-delete-icon" />
                    </div>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          {(loadingDrafts || hasDrafts) && (
            <aside className="report-new-drafts" aria-label="Customer drafts">
              {loadingDrafts ? (
                <p className="report-new-drafts-status">Looking for drafts…</p>
              ) : (
                <>
                  <div className="report-new-drafts-header">
                    <span className="report-new-drafts-label">Open drafts</span>
                    <span className="report-new-drafts-count">{foundDrafts.length}</span>
                  </div>
                  <ul className="report-new-drafts-list">
                    {foundDrafts.map((draft) => (
                      <li key={draft.id}>
                        <div
                          role="button"
                          tabIndex={0}
                          className="report-new-draft-row"
                          onClick={() => loadDraft(draft.url)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              loadDraft(draft.url);
                            }
                          }}
                        >
                          <div className="report-new-draft-main">
                            <div className="report-new-draft-title-line">
                              <span className="report-new-draft-title">{draft.title}</span>
                              {draft.author && (
                                <span className="report-new-draft-author">by {draft.author}</span>
                              )}
                            </div>
                            {draft.updated && (
                              <span className="report-new-draft-meta">{draft.updated}</span>
                            )}
                          </div>
                          {draft.can_delete !== false && (
                            <button
                              type="button"
                              className="report-new-draft-delete"
                              aria-label={`Delete draft ${draft.title}`}
                              onClick={(e) => deleteDraft(e, draft)}
                            >
                              <DeleteOutlineIcon className="report-new-draft-delete-icon" />
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </aside>
          )}
        </div>
      )}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    accounts: state.accounts,
    user: state.user,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportNew);
